import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material"
import { FilterHeader } from "../../components/filterHeader"
import { useStyles } from "./styles"
import { allColumns, downloadHeaderColumns, MONTH_KEYS, monthFields, useInput } from "./constants"
import { FilterHeaderRef } from "../../components/filterHeader/types"
import { createRef, useLayoutEffect, useState, useEffect } from "react"
import { currentWeek } from "../methods/calcDates"
import { intervaleList } from "../../../../../../utils/constants/intervaleList"
import CollapsibleTable from "../../../../../../components/collapsibleTable"
import { DinamicForm } from "../../../../../../components/dinamicForm"
import { dashboardService, storeServices } from "../../../../../../utils/constants/callService"
import { ObjectGeneric, onChange } from "../../../../../../utils/types/generals"
import { formatResultTabulatorEndpoint } from "./methods/formatResultTabulatorEndpoint"

import { RedirectedIds } from "../../../../typing"
import Loader from "../../../../../../components/loader"
import { formatTabulatorAggsResponse, TabulatorRow } from "./methods/formatTabulatorResponse"
import DownloadFiles from "../../../../../../components/donwloadFiles"
import { useTranslation } from "react-i18next"
import { Constants } from "../../../../../../utils/constants"
import dayjs from "dayjs"
import { icons } from "../../../../../../utils/icons"
import { useFilter } from "../../../../../../hooks/filter"
import { FilterHeaderExternal } from "../../components/filterHeaderWithExternal"

const ResultsTabulator = () => {
  const defaultDates = {
    from: currentWeek().start.getTime(),
    to: currentWeek().end.getTime(),
  }
  const styles = useStyles()
  const ref = createRef<FilterHeaderRef>()
  const { t } = useTranslation()
  const { input, items, externalValidation, removeValues, externalInput } = useInput()
  const [data, setData] = useState<any[]>([])
  const [dataToDownload, setDataToDownload] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [lastUpdate, setLastUpdate] = useState('')
  const [lastFilterValues, setLastFilterValues] = useState<ObjectGeneric>(defaultDates)
  const [storesHierarchy, setStoresHierarchy] = useState<any[]>([])
  const externalFilter = useFilter({ formatFilter: {}, defaultValues: defaultDates, externalValidation: externalValidation })
  const [validationToExpand, setValidationToExpand] = useState(false)

  const onChange: onChange = ({ target }) => {
    const { name, value } = target

    const updated = {
      ...externalFilter.filterData,
      [name]: value,
    }

    externalFilter.onChange({ target })

    const formattedPayload = formatResultTabulatorEndpoint(updated)

    handleResultTabulator(
      formattedPayload,
      updated?.intervale?.value || '1m',
      updated
    )
  }

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const stores = await storeServices.getAllStores();
        setStoresHierarchy(stores);
      } catch (e) {
        setStoresHierarchy([]);
      }
    };
    fetchStores();
  }, []);

  const handleResultTabulator = async (payload: ObjectGeneric, intervale: string, newData?: Record<string, unknown>) => {
    setLoading(true)
    try {
      let unitIds: any[] = []
      unitIds = storesHierarchy.map((store: any) => store.id)

      let filteredPayload = { ...payload }
      if (!filteredPayload.query) filteredPayload.query = {}
      if (!filteredPayload.query.bool) filteredPayload.query.bool = {}
      if (!Array.isArray(filteredPayload.query.bool.filter)) filteredPayload.query.bool.filter = []

      filteredPayload.query.bool.filter = filteredPayload.query.bool.filter.filter((f: any) => {
        if (!f || typeof f !== 'object') return true
        if (f.terms && (f.terms.unidad || f.terms.unidad_id)) return false
        return true
      })

      if (newData && newData.units && Array.isArray(newData.units) && newData.units.length > 0) {
        filteredPayload.query.bool.filter.push({ terms: { unidad_id: newData.units.map(u => u.value) } })
      } else if (unitIds.length > 0) {
        filteredPayload.query.bool.filter.push({ terms: { unidad_id: unitIds } })
      }

      const response = await dashboardService['resultTabulatorWeekly']({
        body: filteredPayload
      })

      const hasFilters =
        (Array.isArray(newData?.divisions) && newData?.divisions.length > 0) ||
        (Array.isArray(newData?.units) && newData?.units.length > 0) ||
        (Array.isArray(newData?.zones) && newData?.zones.length > 0)

      if (hasFilters) {
        setValidationToExpand(true)
      } else {
        setValidationToExpand(false)
      }

      const formatted = formatTabulatorAggsResponse(response.aggregations)
      setDataToDownload(addPercentToTabulatorMonths(formatted))
      setData(formatted)
      let semana = ''
      let fecha = ''
      let fechas: string[] = [];
      if (Array.isArray(formatted) && formatted.length > 0) {
        const firstDivision = formatted[0]
        if (Array.isArray(firstDivision.subRows) && firstDivision.subRows.length > 0) {
          const firstZona = firstDivision.subRows[0]
          if (Array.isArray(firstZona.subRows) && firstZona.subRows.length > 0) {
            const firstUnidad = firstZona.subRows[0]
            if (firstUnidad.semana) semana = firstUnidad.semana

            for (const unidad of firstZona.subRows) {
              if (unidad.fecha) fechas.push(unidad.fecha)
            }
          }
        }
      }
      if (fechas.length > 0) {
        fecha = fechas.reduce((a, b) => (new Date(a) > new Date(b) ? a : b))
      }
      let lastUpdateText = ''
      if (semana && fecha) lastUpdateText = `Semana: ${semana}: ${dayjs(fecha).locale('es').format(Constants.dayjsHumanFormat)}`
      else if (semana) lastUpdateText = `Semana: ${semana}`
      else if (fecha) lastUpdateText = `Fecha: ${fecha}`
      else lastUpdateText = ''
      setLastUpdate(lastUpdateText)
    } catch (error) {
      console.error('Error loading ResultTabulator data:', error)
      setData([])
      setDataToDownload([])
    } finally {
      setLoading(false)
    }
  }


  const isNumeric = (value: unknown): value is number | string => {
    if (typeof value === 'number') return true
    if (typeof value === 'string' && value.trim() !== '') {
      return !isNaN(Number(value))
    }
    return false
  }

  const addPercentToTabulatorMonths = (
    rows: TabulatorRow[]
  ): TabulatorRow[] => {
    return rows.map(row => {
      const formattedRow: TabulatorRow = { ...row }

      MONTH_KEYS.forEach(month => {
        const value = formattedRow[month]

        if (isNumeric(value)) {
          const num = Number(value)
          formattedRow[month] = `${num.toFixed(2)}%`
        }
      })

      if (Array.isArray(row.subRows)) {
        formattedRow.subRows = addPercentToTabulatorMonths(row.subRows)
      }

      return formattedRow
    })
  }


  const callResultTabulator = (payload?: { dafaultValue: ObjectGeneric }) => {
    const defaultValues: ObjectGeneric = payload?.dafaultValue || (ref.current?.getValues ? ref.current?.getValues() : {})
    setLastFilterValues(defaultValues)
    const formattedPayload = formatResultTabulatorEndpoint(defaultValues)
    handleResultTabulator(formattedPayload, defaultValues?.intervale?.value || '1m')
  }

  const getFilter = (payload: { dafaultValue: ObjectGeneric }) => {
    callResultTabulator(payload)
  }

  const refresh = (payload: { dafaultValue: ObjectGeneric }) => {
    callResultTabulator(payload)
  }

  useLayoutEffect(() => {
    if (items.unit) {
      callResultTabulator({ dafaultValue: defaultDates })
    }
  }, [items.unit])

  return (
    <Grid key={'results-tabulator'} item xs={12}>
      <div id={RedirectedIds.RESULTS_TABULADOR} />
      <Box sx={styles.card}>
        <Box sx={styles.headerInputs}>
          <Box sx={styles.headerTitle}>
            <Typography sx={styles.titleHeaders}>Tabulador de resultados</Typography>
            <Typography sx={styles.lastUpdate}>
              {t('general.lastUpdate')}: {lastUpdate || '-'}
            </Typography>
          </Box>
          <Box sx={{ ...styles.headerActions }}>
            <Box sx={{ height: '100%', alignItems: 'center', display: 'flex', marginTop: '20px' }}>
              <DownloadFiles
                row={dataToDownload}
                column={downloadHeaderColumns}
                name={"Tabulador de resultados"}
                excludedWord
                excludedPdf
              />
            </Box>
            <FilterHeaderExternal
              countDefault={1}
              filter={externalFilter}
              hiddenRefresh
              cleanFilter={removeValues}
              ref={ref}
              defaultValues={defaultDates}
              title="Filtros del tabulador de resultados"
              externalValidation={externalValidation}
              input={input}
              filterData={getFilter}
              refresh={refresh}
            />
            <Tooltip title={t('ToolTips.refresh')}>
              <IconButton
                sx={styles.refresh}
                onClick={() => {
                  refresh({ dafaultValue: ref.current?.getValues ? ref.current?.getValues() : defaultDates })
                }}
              >
                {icons("refresh")}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {loading ? (<Loader />) :
          <>
            <Box sx={{ mt: 3 }}>
              <DinamicForm input={Object.values(externalInput)} onChange={onChange} values={externalFilter.filterData} />
            </Box>
            <Box sx={{ mt: 3 }}>
              <CollapsibleTable
                validationToExpand={validationToExpand}
                columns={{
                  columns: (() => {
                    const selectedMonths = lastFilterValues?.month
                    if (Array.isArray(selectedMonths) && selectedMonths.length > 0) {
                      const selectedFields = monthFields.filter(m => selectedMonths.some((sel: any) => sel.value === m.value)).map(m => m.field)
                      return [
                        allColumns[0],
                        ...allColumns.filter(col => selectedFields.includes(col.field)),
                        allColumns[allColumns.length - 2],
                        allColumns[allColumns.length - 1],
                      ]
                    }
                    return allColumns
                  })(),
                  data,
                }}
                customHeaderGradient
                sx={{
                  '& .MuiTableRow-hover:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              />
            </Box>
          </>
        }
      </Box>
    </Grid>
  )
}


export { ResultsTabulator }